import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginResponse, RefreshTokenResponse } from '../../models/auth.interface';
import { NotificationService } from './toast-service.service';

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly loginCompletedSubject = new Subject<void>();
  readonly loginCompleted$: Observable<void> = this.loginCompletedSubject.asObservable();
  private static readonly refreshLockKey = 'chatlink.auth.refresh.lock';
  private static readonly refreshResultKey = 'chatlink.auth.refresh.result';
  private static readonly sessionEventKey = 'chatlink.auth.session.event';
  private static readonly refreshLockLeaseMs = 20_000;
  private static readonly refreshWaitMs = 25_000;
  private readonly apiUrl = environment.apiUrl;
  private readonly tabId = this.createTabId();
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshPromise: Promise<boolean> | null = null;
  private csrfPromise: Promise<boolean> | null = null;
  private csrfToken: string | null = null;
  private sessionExpiredHandled = false;
  private readonly refreshWaiters = new Set<(result: RefreshCoordinationResult) => void>();

  constructor(private http: HttpClient, private router: Router, private toastService: NotificationService) {
    window.addEventListener('storage', this.handleStorageEvent);
  }

  isAuthenticated(): boolean { return sessionStorage.getItem('authenticated') === 'true'; }
  isAccessTokenExpired(leewayMs = 0): boolean {
    if (!this.isAuthenticated()) return true;
    const rawExpiresAt = sessionStorage.getItem('tokenExpiresAt');
    if (!rawExpiresAt) return false;
    const expiresAt = Date.parse(rawExpiresAt);
    return Number.isFinite(expiresAt) && Date.now() + leewayMs >= expiresAt;
  }

  start(expiresAt?: string | null, refreshAt?: string | null): void {
    this.sessionExpiredHandled = false; this.clearRefreshTimer();
    const scheduledRefresh = refreshAt ? Date.parse(refreshAt) : NaN;
    const parsedExpiry = expiresAt ? Date.parse(expiresAt) : NaN;
    const refreshTime = Number.isFinite(scheduledRefresh)
      ? scheduledRefresh
      : Number.isFinite(parsedExpiry) ? parsedExpiry - 60_000 : NaN;
    const delay = Number.isFinite(refreshTime) ? Math.max(5_000, refreshTime - Date.now()) : 10 * 60_000;
    this.refreshTimer = setTimeout(() => void this.refreshToken(), delay);
  }
  stop(): void {
    this.clearRefreshTimer();
    this.refreshPromise = null;
    this.csrfPromise = null;
    this.csrfToken = null;
  }
  storeAuthData(response: LoginResponse): void {
    this.sessionExpiredHandled = false; sessionStorage.setItem('authenticated', 'true');
    if (response.expiresAt) sessionStorage.setItem('tokenExpiresAt', response.expiresAt);
    if (response.refreshAt) sessionStorage.setItem('tokenRefreshAt', response.refreshAt);
    else sessionStorage.removeItem('tokenRefreshAt');
    if (response.nombreUsuario) sessionStorage.setItem('username', response.nombreUsuario);
    if (response.nombreCompleto) sessionStorage.setItem('nombreCompleto', response.nombreCompleto);
  }
  notifyLoginCompleted(): void { this.loginCompletedSubject.next(); }
  async refreshToken(): Promise<boolean> {
    if (this.refreshPromise) return this.refreshPromise;
    this.refreshPromise = this.refreshTokenWithCrossTabCoordination()
      .finally(() => { this.refreshPromise = null; });
    return this.refreshPromise;
  }
  async ensureCsrfToken(force = false): Promise<boolean> {
    if (!force && this.hasCsrfCookie()) return true;
    if (this.csrfPromise) return this.csrfPromise;
    this.csrfPromise = firstValueFrom(
      this.http.get<{ token?: string }>(`${this.apiUrl}/login/csrf`, { withCredentials: true }),
    )
      .then(response => {
        this.csrfToken = response?.token || null;
        // The response token is also kept in memory because a cross-origin
        // frontend cannot read a cookie belonging to the API host.
        return this.hasCsrfCookie() || !!this.csrfToken;
      })
      .catch(() => false)
      .finally(() => { this.csrfPromise = null; });
    return this.csrfPromise;
  }
  getCsrfToken(): string | null { return this.csrfToken ?? this.readCsrfCookie(); }
  async getValidToken(): Promise<string | null> {
    if (!this.isAuthenticated()) return null;
    if (this.isAccessTokenExpired(5_000)) {
      return (await this.refreshToken()) ? 'cookie-session' : null;
    }
    return 'cookie-session';
  }
  expireSession(): void {
    this.requireLogin('Sesion expirada. Por favor, inicia sesion nuevamente.', 'error');
  }
  requireLogin(message: string, type: 'success' | 'error' = 'success'): void {
    if (this.sessionExpiredHandled) return;
    this.sessionExpiredHandled = true; this.clearRefreshTimer(); this.csrfToken = null; sessionStorage.clear();
    this.publishSessionEvent('expired');
    window.dispatchEvent(new Event('chatlink-session-expired'));
    this.toastService.notify(message, type); this.router.navigate(['/login']);
  }
  async logout(): Promise<void> {
    await this.ensureCsrfToken();
    await firstValueFrom(this.http.post<void>(`${this.apiUrl}/login/logout`, null, { withCredentials: true }))
      .catch(() => undefined);
    this.stop();
    sessionStorage.clear();
  }
  private hasCsrfCookie(): boolean {
    return !!this.readCsrfCookie();
  }
  private readCsrfCookie(): string | null {
    if (typeof document === 'undefined') return null;
    const cookie = document.cookie
      .split(';')
      .map(value => value.trim())
      .find(value => value.startsWith('XSRF-TOKEN='));
    if (!cookie) return null;

    try {
      return decodeURIComponent(cookie.substring('XSRF-TOKEN='.length));
    } catch {
      return cookie.substring('XSRF-TOKEN='.length);
    }
  }
  private clearRefreshTimer(): void { if (this.refreshTimer) clearTimeout(this.refreshTimer); this.refreshTimer = null; }

  private async refreshTokenWithCrossTabCoordination(): Promise<boolean> {
    const deadline = Date.now() + AuthSessionService.refreshWaitMs;

    while (Date.now() < deadline) {
      if (this.tryAcquireRefreshLock()) {
        try {
          return await this.performRefreshToken();
        } finally {
          this.releaseRefreshLock();
        }
      }

      const result = await this.waitForRefreshResult(Math.min(1_000, deadline - Date.now()));
      if (result) {
        if (result.ok && result.response) {
          this.storeAuthData(result.response);
          this.start(result.response.expiresAt, result.response.refreshAt);
          return true;
        }
        if (!result.ok) {
          this.expireSession();
        }
        return false;
      }
    }

    // A crashed tab can leave a lease behind. Once its lease expires, take over.
    if (this.tryAcquireRefreshLock()) {
      try {
        return await this.performRefreshToken();
      } finally {
        this.releaseRefreshLock();
      }
    }

    this.expireSession();
    return false;
  }

  private async performRefreshToken(): Promise<boolean> {
    try {
      const response = await firstValueFrom(
        this.http.post<RefreshTokenResponse>(
          `${this.apiUrl}/login/refresh-token`,
          null,
          { withCredentials: true },
        ),
      );
      if (!response) {
        this.publishRefreshResult({ ok: false, at: Date.now() });
        this.expireSession();
        return false;
      }
      this.storeAuthData(response);
      this.start(response.expiresAt, response.refreshAt);
      this.publishRefreshResult({ ok: true, at: Date.now(), response });
      return true;
    } catch {
      this.publishRefreshResult({ ok: false, at: Date.now() });
      this.expireSession();
      return false;
    }
  }

  private tryAcquireRefreshLock(): boolean {
    const now = Date.now();
    const existing = this.readRefreshLock();
    if (existing && existing.expiresAt > now && existing.ownerId !== this.tabId) return false;

    const candidate: RefreshLock = { ownerId: this.tabId, expiresAt: now + AuthSessionService.refreshLockLeaseMs };
    try {
      localStorage.setItem(AuthSessionService.refreshLockKey, JSON.stringify(candidate));
      return this.readRefreshLock()?.ownerId === this.tabId;
    } catch {
      return true;
    }
  }

  private releaseRefreshLock(): void {
    try {
      if (this.readRefreshLock()?.ownerId === this.tabId) {
        localStorage.removeItem(AuthSessionService.refreshLockKey);
      }
    } catch {
      // Storage may be unavailable in private browsing; the local promise still protects this tab.
    }
  }

  private readRefreshLock(): RefreshLock | null {
    try {
      const raw = localStorage.getItem(AuthSessionService.refreshLockKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<RefreshLock>;
      return typeof parsed.ownerId === 'string' && typeof parsed.expiresAt === 'number'
        ? { ownerId: parsed.ownerId, expiresAt: parsed.expiresAt }
        : null;
    } catch {
      return null;
    }
  }

  private waitForRefreshResult(timeoutMs: number): Promise<RefreshCoordinationResult | null> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (result: RefreshCoordinationResult | null) => {
        if (settled) return;
        settled = true;
        this.refreshWaiters.delete(waiter);
        clearTimeout(timeout);
        resolve(result);
      };
      const waiter = (result: RefreshCoordinationResult) => finish(result);
      const timeout = setTimeout(() => finish(null), Math.max(0, timeoutMs));
      this.refreshWaiters.add(waiter);
    });
  }

  private readonly handleStorageEvent = (event: StorageEvent): void => {
    if (event.key === AuthSessionService.refreshResultKey && event.newValue) {
      try {
        const result = JSON.parse(event.newValue) as RefreshCoordinationResult;
        if (typeof result.ok === 'boolean' && typeof result.at === 'number') {
          this.refreshWaiters.forEach((waiter) => waiter(result));
        }
      } catch {
        // Ignore malformed coordination messages from stale clients.
      }
    }

    if (event.key === AuthSessionService.sessionEventKey && event.newValue === 'expired') {
      this.expireSessionLocally();
    }
  };

  private publishRefreshResult(result: RefreshCoordinationResult): void {
    try {
      localStorage.setItem(AuthSessionService.refreshResultKey, JSON.stringify(result));
    } catch {
      // The owning tab still has the refreshed cookies and session data.
    }
  }

  private publishSessionEvent(event: 'expired'): void {
    try { localStorage.setItem(AuthSessionService.sessionEventKey, event); } catch { /* best effort */ }
  }

  private expireSessionLocally(): void {
    if (this.sessionExpiredHandled) return;
    this.sessionExpiredHandled = true;
    this.clearRefreshTimer();
    this.csrfToken = null;
    sessionStorage.clear();
    window.dispatchEvent(new Event('chatlink-session-expired'));
    this.router.navigate(['/login']);
  }

  private createTabId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

interface RefreshLock { ownerId: string; expiresAt: number; }
interface RefreshCoordinationResult {
  ok: boolean;
  at: number;
  response?: RefreshTokenResponse;
}
